import { debounce } from "lodash";
import { useEffect, useState } from "react";
import { ICartSearchResponseData } from "@/api_framework/api_modals/Cart";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import { useCartSearchAPI } from "@/app/hooks/api_hooks/Cart/useCartSearchAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "@uidotdev/usehooks";
import clsx from "clsx";
import { Fragment } from "react";
import { useLoader } from "@/app/hooks/useLoader";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";

const CartProductSearchComponent: React.FC<{
  containerClassName?: string;
  placeholder?: string;
  inputContainerClassName?: string;
  itemActiveClassName?: string;
  onSelectItem?: (item: ICartSearchResponseData) => void;
}> = ({ containerClassName, inputContainerClassName, onSelectItem, ...props }) => {
  const { getRouteKey } = useRouter();
  const { data: searchResults, execute: searchCartProducts, clearSearchResults } = useCartSearchAPI();
  const [selected, setSelected] = useState();
  const [isBarcodeScan, setIsBarcodeScan] = useState(false);
  const [lastInputTime, setLastInputTime] = useState<number | null>(null);
  const mappedClinicID = useAppSelector((state) => state?.root?.UIStates?.mappedClinicId);
  const customerProfileState = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"))?.routeState?.state as
    | ICustomerProfileState
    | undefined;
  const petId = customerProfileState?.petId;
  const customerId = customerProfileState?.customerId;
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query?.trim(), 300);
const { execute: logAPI } = useClickLogAPI();
  const cartState = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id"))?.routeState?.state as ICartState | undefined;
  const activeCartDetails = cartState?.activeCartDetails;
  const activeCartId = cartState?.activeCartId;
    const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
      const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const callLogAPI = (searchString: string, searchData?:any,clickItem?:any) => {
    const logBody: IClickLogBody = {
      event_type:clickItem?"CART_CLICK":searchData?"CART_VIEW":"CART_SEARCH",
      scr_id: "CART",
      client_id: customerId,
      emr_id: activeCartDetails?.emr_id,
      cart_id:activeCartId,
      pet_id: petId,
      search_item: searchString,
      search_result: searchData?searchData:undefined,
      click_on_search_item: clickItem?clickItem:undefined,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now()
    };
    logAPI(logBody);
  }
     
  const debounceSearch = debounce(() => {
    searchCartProducts(debouncedQuery, mappedClinicID, petId).then(({status,message})=>{
      if(status){
        callLogAPI(debouncedQuery,message)
      }else{
        callLogAPI(debouncedQuery,[])
      }
      
    })
  }, 500);

  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      debounceSearch();
    } else {
      clearSearchResults();
    }
  }, [debouncedQuery]);

  const isSearchPending = useLoader("isCustomerCartItemsSearchInProgress");

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const now = Date.now();
    if (lastInputTime && now - lastInputTime < 50) {
      setIsBarcodeScan(true);
    } else {
      setIsBarcodeScan(false);
    }
    setLastInputTime(now);
    if (event.key === "Enter" && query.length >= 6) {
      setIsBarcodeScan(true);
    }
  };

  useEffect(() => {
    if (isBarcodeScan && searchResults?.length === 1) {
      onSelectItem?.(searchResults[0]);
      setQuery("");
      clearSearchResults();
    }
  }, [isBarcodeScan, searchResults]);

  return (
    <div className={clsx(containerClassName)}>
      <Combobox
        value={selected}
        onChange={(item) => {
          if (item) {
            setSelected(item);
            onSelectItem?.(item);
            callLogAPI(debouncedQuery,null,item)
            
          }
        }}
      >
        <div className="relative mt-1 w-full">
          <div className="relative border-gray-200 rounded-l-none bg-white border rounded-lg focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 w-full text-left sm:text-sm  overflow-hidden focus:outline-none">
            <div className="flex justify-start items-center">
              {query?.length >= 3 ? (
                <button
                  type="button"
                  className="p-2"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setQuery("");
                    clearSearchResults();
                    setIsBarcodeScan(false);
                  }}
                >
                  <XMarkIcon
                    className="w-4 h-4 text-red-500  cursor-pointer hover:text-red-800"
                  />
                </button>
              ) : (
                <span className="p-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-500" />
                </span>
              )}
              <Combobox.Button className={clsx(inputContainerClassName)}>
                <Combobox.Input
                  autoComplete="off"
                  autoCorrect="off"
                  autoFocus
                  value={query}
                  placeholder={props?.placeholder ?? "Start Searching ..."}
                  className="py-2 pr-10 pl-3 border-none focus:ring-0 w-full text-gray-900 text-sm leading-5"
                  onChange={(event) => setQuery(event.target.value)}
                  onBlur={() => onSelectItem?.(null as any)} // Prevents auto-selection
                  onKeyDown={handleKeyPress} // Detect fast input
                />
              </Combobox.Button>
            </div>
            <Combobox.Button className="right-0 absolute inset-y-0 flex items-center pr-2">
              <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Combobox.Options className="z-20  absolute  bg-white shadow-lg mt-1  rounded-md ring-1 ring-black/5  max-h-96 w-full text-base sm:text-sm overflow-auto focus:outline-none">
              {searchResults.length === 0 && query !== "" ? (
                <div className="relative px-4 py-2 text-gray-700 cursor-default select-none">
                  {isSearchPending ? <CircleSpinnerLoader h="20" w="20" /> : <p>No Result Found.</p>}
                </div>
              ) : (
                searchResults.map((result, idx) => (
                  <Combobox.Option
                    key={`${result?.id}:${idx}`}
                    className={({ active }) =>
                      ` relative cursor-default select-none mt-2 p-4 ${
                        active
                          ? props?.itemActiveClassName
                            ? props?.itemActiveClassName
                            : "bg-teal-500 text-white"
                          : "text-gray-900"
                      } rounded-md border shadow-md transition-colors ml-1 `
                    }
                    value={result}
                  >
                    {({ selected }) => (
                      <div className="flex  justify-between items-center gap-x-2 ">
                        <div className="mt-2 w-[70%]  flex justify-start items-center ">
                          <span className={`block text-[13px]  ${selected ? "font-medium" : "font-normal"}  `}>
                            {result.name?.split("_")?.join(" ")}
                          </span>
                        </div>
                        <div className="flex gap-x-3  mt-4">
                          {result?.stock_qty_with_text &&
                          result?.stock_qty_with_text?.toLocaleLowerCase() === "unavailable" ? (
                            <SimpleBadge
                              colorClassName="text-orange-600 bg-orange-50 ring-orange-500/10"
                              label={result?.stock_qty_with_text}
                            />
                          ) : (
                            <SimpleBadge
                              colorClassName="text-green-600 bg-green-50 ring-green-500/10"
                              label={result?.stock_qty_with_text}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

export default CartProductSearchComponent;
